// src/types/api.ts
import { z } from 'zod';

// ================== ENUMS (allineati con backend) ==================
export enum RequestStatus {
  New = 1,
  InProgress = 2,
  PendingCompletion = 3,
  Completed = 4,
  Cancelled = 5,
  // Stato intermedio: richiesta sospesa ma riprendibile (riportabile In Lavorazione).
  // Stato interno di Ufficio Stile, non innesca notifiche ai portali esterni.
  Suspended = 6,
  // Fase preliminare della Campionatura, gestita da Ufficio Stile. Valida solo per
  // RequestType.Campionatura; le richieste create prima dell'introduzione di questa fase
  // (legacy) non la attraversano mai.
  PreCampionatura = 7,
  // La Pre-Campionatura è terminata: la richiesta è "in attesa" che Industrializzazione
  // la prenda in carico avviando la lavorazione (InProgress).
  PreCampionaturaCompleted = 8
}

export enum RequestType {
  Bozzetto = 1,
  Campionatura = 2,
  Produzione = 3,
  Gamma = 4
}

export enum TaskStatus {
  Ideas = 1,
  ToDo = 2,
  Doing = 3,
  Testing = 4,
  Done = 5,
  Cancelled = 6
}

export enum TaskPriority {
  Low = 1,
  Medium = 2,
  High = 3,
  Critical = 4
}

export enum UserRole {
  Admin = 1,
  AdminUfficioStile = 2,
  OperatoreUfficioStile = 3,
  AdminSviluppo = 4,
  OperatoreSviluppo = 5,
  CapoUfficioStile = 2, // Alias for AdminUfficioStile
  UfficioStile = 3, // Alias for OperatoreUfficioStile
  CapoIndustrializzazione = 4, // Alias for AdminSviluppo
  Industrializzazione = 5, // Alias for OperatoreSviluppo
  User = 99
}

export enum RequestPriority {
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  URGENT = 4
}

export enum CommentVisibility {
  TaskOnly = 1,
  RequestLevel = 2,
  ExternalPortal = 3
}

export enum AttachmentType {
  Image = 1,
  Document = 2,
  Video = 3,
  Other = 4
}

export enum KanbanColumn {
  ToDo = 1,
  InProgress = 2,
  Done = 3
}

export enum ArticleStatus {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED',
  DELETED = 'DELETED'
}

export enum ExternalDataType {
  CLIENT = 'CLIENT',
  PRODUCT = 'PRODUCT',
  INVENTORY = 'INVENTORY',
  FINANCIAL = 'FINANCIAL',
  ANALYTICS = 'ANALYTICS',
  INTEGRATION = 'INTEGRATION'
}

export enum CommentType {
  GENERAL = 'GENERAL',
  ISSUE = 'ISSUE',
  SUGGESTION = 'SUGGESTION'
}

export enum AttachmentType {
  DOCUMENT = 'DOCUMENT',
  IMAGE = 'IMAGE',
  VIDEO = 'VIDEO',
  OTHER = 'OTHER'
}

// ================== BASE TYPES ==================
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

export interface PagedResponse<T> {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface PaginationParams {
  pageNumber: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface SearchParams extends PaginationParams {
  searchTerm?: string;
  filters?: Record<string, any>;
}

// ================== USER TYPES (allineati con backend) ==================
export interface User extends BaseEntity {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  isActive: boolean;
  lastLoginAt?: string;
  
  // Contatori per statistiche
  assignedTasksCount: number;
  completedTasksCount: number;
  createdRequestsCount: number;
}

export interface UserCreateRequest {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  password: string;
}

export interface UserUpdateRequest {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  isActive?: boolean;
}

// ================== AUTH TYPES ==================
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// ================== CLIENT TYPES ==================
export interface Client extends BaseEntity {
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  isActive: boolean;
  isExternal: boolean;
  externalId?: string;
}

export interface ClientCreateRequest {
  name: string;
  code: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  isActive: boolean;
}

export interface ClientUpdateRequest {
  name?: string;
  code?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  isActive?: boolean;
}

// ================== ARTICLE TYPES ==================
export interface Article extends BaseEntity {
  code: string;
  description: string;
  category?: string;
  price?: number;
  isActive: boolean;
  isExternal: boolean;
  externalId?: string;
  stockQuantity?: number;
  unit?: string;
  notes?: string;
}

export interface ArticleCreateRequest {
  code: string;
  description: string;
  category?: string;
  price?: number;
  isActive: boolean;
  stockQuantity?: number;
  unit?: string;
  notes?: string;
}

export interface ArticleUpdateRequest {
  code?: string;
  description?: string;
  category?: string;
  price?: number;
  isActive?: boolean;
  stockQuantity?: number;
  unit?: string;
  notes?: string;
}

// ================== REQUEST TYPES (allineati con backend) ==================
export interface Request extends BaseEntity {
  // Cliente - approccio ibrido
  title?: string;
  clientId?: string;
  clientName: string;
  externalClient?: ExternalClient;

  requestType: RequestType;
  status: RequestStatus;
  deliveryDate?: string;
  notes?: string;

  // Request Number (interno - BOZ001, etc.)
  requestNumber?: string;

  // Progressive Number (N/ANNO)
  progressiveNumber?: number;
  progressiveYear?: number;
  progressiveNumberFormatted?: string;

  // Utente creatore
  createdByUserId?: string;
  createdByUser?: User;

  // Cross-portal fields
  createdByEmail?: string;
  areaManagerEmail?: string;
  crossPortalRequestId?: string;
  personalizzatiRequestId?: string;
  isFromCrossPortal?: boolean;
  lastCrossPortalSync?: Date | null;
  isLinkedToCrossPortal?: boolean;
  requiresCrossPortalSync?: boolean;

  // Relazioni
  articles: RequestArticle[];
  tasks: ProjectTask[];
  comments: Comment[];
  attachments: Attachment[];

  // Statistiche
  tasksCount: number;
  completedTasksCount: number;
  commentsCount: number;
  attachmentsCount: number;
  isOverdue: boolean;
  daysUntilDelivery: number;
  completionPercentage: number;
  completedAt?: string;
  /** Data di completamento della fase di Pre-Campionatura (solo Campionatura). */
  preCampionaturaCompletedAt?: string;
}

export interface RequestCreateRequest {
  title?: string;
  clientName: string;
  clientId?: string;
  requestType: RequestType | string;
  deliveryDate?: string;
  notes?: string;
  articles: RequestArticleCreateRequest[];
  createdByUserId?: string;
  // Progressive Number (opzionale, auto-generato se null)
  progressiveNumber?: number;
  progressiveYear?: number;
}

export interface RequestUpdateRequest {
  title?: string;
  clientName: string;
  clientId?: string;
  requestType: RequestType | string;
  deliveryDate?: string;
  notes?: string;
  articles: RequestArticleCreateRequest[];
  // Progressive Number (opzionale per modifica)
  progressiveNumber?: number;
  progressiveYear?: number;
  // Data creazione (opzionale per modifica)
  createdAt?: string;
  // Data completamento (opzionale per modifica)
  completedAt?: string;
  // Data di completamento della fase di Pre-Campionatura (opzionale per modifica/correzione)
  preCampionaturaCompletedAt?: string;
}

// Type aliases for compatibility with API service
export type CreateRequestData = RequestCreateRequest;
export type UpdateRequestData = RequestUpdateRequest;

// Request filters for API
export interface RequestFilters {
  status?: RequestStatus;
  requestType?: RequestType;
  clientName?: string;
  clientId?: string;
  startDate?: Date;
  endDate?: Date;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  progressiveYear?: number;
  projectNumber?: string;
}

// Aggregati richieste per dashboard/contatori (allineato con RequestAggregatesDto BE)
export interface RequestAggregates {
  byType: Record<RequestType, number>;
  byTypeAndStatus: TypeStatusBucket[];
  monthlyTrend: MonthlyTrendBucket[];
  topClients: TopClientBucket[];
  completion: CompletionStats;
  availableYears: number[];
  totalCount: number;
}

export interface TypeStatusBucket {
  type: RequestType;
  status: RequestStatus;
  count: number;
}

export interface MonthlyTrendBucket {
  year: number;
  month: number;
  type: RequestType;
  count: number;
}

export interface TopClientBucket {
  clientId?: number;
  clientName: string;
  count: number;
}

export interface CompletionStats {
  // Per la Campionatura, questi valori misurano solo la fase Campione (vedi preCampionatura
  // sotto per la fase precedente); per le richieste legacy senza Pre-Campionatura coincidono
  // con il tempo totale.
  averageDaysByType: Record<RequestType, number>;
  medianDaysByType: Record<RequestType, number>;
  p90DaysByType: Record<RequestType, number>;
  overallAverageDays: number;
  /** Statistiche sulla fase di Pre-Campionatura (solo Campionatura), assente se non ci sono dati. */
  preCampionatura?: PreCampionaturaStats;
}

export interface PreCampionaturaStats {
  averageDays: number;
  medianDays: number;
  p90Days: number;
  count: number;
}

export interface RequestAggregatesQuery {
  type?: RequestType;
  year?: number;
  startDate?: string;
  endDate?: string;
}

// Riga richiesta per grafici analytics tempi di completamento + drill-down
// (allineato con RequestAnalyticsRowDto BE)
export interface RequestAnalyticsRow {
  id: number;
  requestNumber: string;
  progressiveNumber: number;
  progressiveYear: number;
  progressiveNumberFormatted: string;
  type: RequestType;
  status: RequestStatus;
  title: string;
  clientId?: number;
  clientName: string;
  createdAt: string;
  completedAt?: string;
  /** Data di completamento della fase di Pre-Campionatura (solo Campionatura). */
  preCampionaturaCompletedAt?: string;
  durationDays?: number;
  /** Giorni di Pre-Campionatura (solo Campionatura), assente se la fase non è completata. */
  preCampionaturaDurationDays?: number;
  /** Giorni della fase Campione. Per le richieste legacy coincide con durationDays. */
  campionaturaDurationDays?: number;
}

export interface AnalyticsRequestsQuery {
  type?: RequestType;
  status?: RequestStatus;
  year?: number;
  startDate?: string;
  endDate?: string;
  /** "created" filtra su data creazione, "completed" su data completamento, "precampionatura" su data fine Pre-Campionatura */
  dateField?: 'created' | 'completed' | 'precampionatura';
  completedOnly?: boolean;
  minDurationDays?: number;
  maxDurationDays?: number;
  clientId?: number;
  clientName?: string;
  /** Solo richieste che hanno completato la fase di Pre-Campionatura */
  preCampionaturaCompletedOnly?: boolean;
}

// ================== REQUEST ARTICLE TYPES (allineati con backend) ==================
export interface RequestArticle extends BaseEntity {
  requestId: string;
  
  // Articolo - approccio ibrido
  articleId?: string;
  articleCode: string;
  articleName: string;
  externalArticle?: ExternalArticle;
  
  quantity: number;
  notes?: string;
  workTypes?: string;
  
  // Relazioni
  request?: Request;
}

export interface RequestArticleCreateRequest {
  articleCode: string;
  articleName: string;
  articleId?: string;
  quantity: number;
  notes?: string;
  workTypes?: string;
}

export interface RequestArticleUpdateRequest {
  articleCode: string;
  articleName: string;
  articleId?: string;
  quantity: number;
  notes?: string;
  workTypes?: string;
}

// ================== TASK TYPES (allineati con backend) ==================
export interface ProjectTask extends BaseEntity {
  name: string;
  title?: string; // Backend può ritornare sia name che title
  description?: string;
  status: TaskStatus;
  statusString?: string; // Backend ritorna anche la versione stringa
  priority: TaskPriority;
  priorityString?: string; // Backend ritorna anche la versione stringa
  
  requestId: string;
  request?: Request;

  /** @deprecated usare assignees */
  assignedToUserId?: number; // Backend ritorna number, non string
  /** @deprecated usare assignees */
  assignedToUser?: User;
  assignees?: TaskAssignee[];

  startDate?: string;
  endDate?: string;
  dueDate?: string; // Backend ritorna dueDate invece di endDate
  
  estimatedHours?: number;
  actualHours?: number;
  completedAt?: string;
  notes?: string;
  
  position?: number;
  tag?: string;
  
  // Relazioni
  checklistItems: ChecklistItem[];
  comments: Comment[];
  attachments: Attachment[];
  
  // Statistiche
  isOverdue: boolean;
  completionPercentage: number;
  checklistItemsCount: number;
  completedChecklistItemsCount: number;
  commentsCount: number;
  attachmentsCount: number;
}

export interface ProjectTaskCreateRequest {
  name: string;
  description?: string;
  requestId: string; // Will be converted to GUID on backend
  priority: TaskPriority;
  assignedToUserId?: string; // Will be converted to GUID on backend
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  dueDate?: string; // ISO string
  tags?: string;
  checklistItems?: Array<{
    name: string;
    completed: boolean;
  }>;
}

export interface ProjectTaskUpdateRequest {
  name: string;
  description?: string;
  priority: TaskPriority;
  assignedToUserId?: string; // Will be converted to GUID on backend
  startDate?: string; // ISO string
  endDate?: string; // ISO string
  dueDate?: string; // ISO string
  tags?: string;
}

export interface UpdateTaskStatusRequest {
  status: TaskStatus;
}

// Filter DTO for tasks
export interface TaskFilterDto {
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedUserId?: string;
  requestId?: string;
  tags?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  pageNumber?: number; // Aggiunto per compatibilità con SearchParams
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ================== CHECKLIST ITEM TYPES (allineati con backend) ==================
export interface ChecklistItem extends BaseEntity {
  name: string;
  description?: string;
  isCompleted: boolean;
  completedAt?: string;
  order: number;
  
  projectTaskId: string;
  projectTask?: ProjectTask;
  completedByUserId?: string;
  completedByUser?: User;
}

export interface ChecklistItemCreateRequest {
  name: string;
  description?: string;
  order?: number;
}

export interface ChecklistItemUpdateRequest {
  name?: string;
  description?: string;
  isCompleted?: boolean;
  order?: number;
}

export interface ChecklistItemCreateRequest {
  name: string;
  description?: string;
  order?: number;
}

export interface ChecklistItemUpdateRequest {
  name?: string;
  description?: string;
  isCompleted?: boolean;
  order?: number;
}

// ================== COMMENT TYPES (allineati con backend) ==================
export interface Comment extends BaseEntity {
  content: string;
  visibility: CommentVisibility;
  authorName?: string;

  requestId?: string;
  request?: Request;

  taskId?: string;
  task?: ProjectTask;

  createdByUserId: string;
  createdByUser?: User;
  
  parentCommentId?: string;
  parentComment?: Comment;
  
  // Relazioni
  replies: Comment[];
  attachments: Attachment[];
}

export interface CommentCreateRequest {
  content: string;
  visibility: CommentVisibility;
  requestId?: string;
  taskId?: string;
  parentCommentId?: string;
}

export interface CommentUpdateRequest {
  content?: string;
  visibility?: CommentVisibility;
}

// ================== ATTACHMENT TYPES ==================
export interface Attachment extends BaseEntity {
  fileName: string;
  originalFileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  type: AttachmentType;
  requestId?: string;
  request?: Request;
  taskId?: string;
  task?: ProjectTask;
  commentId?: string;
  comment?: Comment;
  description?: string;
  downloadUrl?: string;
  isGlobal?: boolean;
}

export interface AttachmentCreateRequest {
  fileName: string;
  originalFileName: string;
  filePath: string;
  fileSize: number;
  mimeType: string;
  type: AttachmentType;
  requestId?: string;
  taskId?: string;
  commentId?: string;
  description?: string;
}

export interface AttachmentUpdateRequest {
  description?: string;
}

// External Data interfaces
export interface ExternalData extends BaseEntity {
  type: ExternalDataType;
  source: string;
  externalId: string;
  data: any;
  requestId?: string;
  request?: Request;
  lastSyncDate?: Date;
  syncStatus: 'PENDING' | 'SUCCESS' | 'FAILED';
  errorMessage?: string;
  metadata?: any;
}

export interface ExternalDataCreateRequest {
  type: ExternalDataType;
  source: string;
  externalId: string;
  data: any;
  requestId?: string;
  metadata?: any;
}

export interface ExternalDataUpdateRequest {
  data?: any;
  syncStatus?: 'PENDING' | 'SUCCESS' | 'FAILED';
  errorMessage?: string;
  metadata?: any;
}

// ================== HOME DASHBOARD TYPES ==================
// Dashboard operativa (home): cosa richiede attenzione oggi, filtrata per ruolo.
// Corrisponde a HomeDashboardDto (UfficioStile.Application/DTOs/Dashboard/HomeDashboardDto.cs).

export type PipelineSectionKey =
  | 'bozzetti'
  | 'pre-campionatura'
  | 'campioni'
  | 'produzione'
  | 'gamma';

export interface HomeDashboardScope {
  seesStile: boolean;
  seesIndustrializzazione: boolean;
  canSeeAnalytics: boolean;
}

export interface HomeKpis {
  openCount: number;
  overdueCount: number;
  dueSoonCount: number;
  createdMonthToDate: number;
  createdPreviousPeriod: number;
  completedMonthToDate: number;
  completedPreviousPeriod: number;
  periodStartUtc: string;
  previousPeriodStartUtc: string;
  previousPeriodEndUtc: string;
}

export interface StatusCount {
  status: RequestStatus;
  count: number;
}

export interface PipelineSection {
  key: PipelineSectionKey;
  type: RequestType;
  openCount: number;
  overdueCount: number;
  statuses: StatusCount[];
}

export interface HomeRequestItem {
  id: number;
  progressiveNumberFormatted: string;
  title: string;
  clientName: string;
  type: RequestType;
  status: RequestStatus;
  dueDate?: string;
  createdAt: string;
  /** Giorni alla scadenza; negativo se la richiesta è già in ritardo. */
  daysToDue?: number;
}

export interface MonthlyFlowPoint {
  year: number;
  month: number;
  created: number;
  completed: number;
}

export interface HomeTaskItem {
  id: number;
  title: string;
  status: string;
  priority: string;
  effectiveDueDate?: string;
  isInternalDueDate: boolean;
  isOverdue: boolean;
  daysToDue?: number;
  requestId: number;
  requestTitle: string;
  requestProgressiveNumberFormatted: string;
  requestType: RequestType;
}

export interface MyTasks {
  openCount: number;
  overdueCount: number;
  dueSoonCount: number;
  items: HomeTaskItem[];
}

export interface HomeDashboard {
  generatedAtUtc: string;
  scope: HomeDashboardScope;
  kpis: HomeKpis;
  pipeline: PipelineSection[];
  overdue: HomeRequestItem[];
  dueSoon: HomeRequestItem[];
  recent: HomeRequestItem[];
  monthlyFlow: MonthlyFlowPoint[];
  myTasks: MyTasks;
}

// ================== CACHE TYPES ==================
export interface CacheInfo {
  key: string;
  value: any;
  expiresAt?: string;
  size?: number;
  hitCount?: number;
  missCount?: number;
  lastAccessed?: string;
}

export interface CacheStats {
  totalKeys: number;
  totalMemoryUsage: number;
  hitRate: number;
  missRate: number;
  evictionCount: number;
  keysByPrefix: Record<string, number>;
}

// ================== VALIDATION SCHEMAS ==================
export const userCreateSchema = z.object({
  username: z.string().min(3).max(50),
  email: z.string().email(),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  role: z.nativeEnum(UserRole),
  phoneNumber: z.string().optional(),
  password: z.string().min(6)
});

export const requestCreateSchema = z.object({
  clientName: z.string().min(1).max(200),
  clientId: z.string().max(50).optional(),
  requestType: z.nativeEnum(RequestType),
  deliveryDate: z.string().optional(),
  notes: z.string().max(1000).optional(),
  articles: z.array(z.object({
    articleCode: z.string().min(1).max(100),
    articleName: z.string().min(1).max(200),
    articleId: z.string().max(50).optional(),
    quantity: z.number().int().min(1),
    notes: z.string().max(500).optional(),
    workTypes: z.string().max(100).optional()
  })).min(1),
  createdByUserId: z.string().uuid().optional()
});

export const taskCreateSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  priority: z.nativeEnum(TaskPriority),
  requestId: z.string().uuid(),
  assignedToUserId: z.string().uuid().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  position: z.number().int().min(0),
  tag: z.string().optional()
});

export const commentCreateSchema = z.object({
  content: z.string().min(1).max(2000),
  visibility: z.nativeEnum(CommentVisibility),
  requestId: z.string().uuid().optional(),
  taskId: z.string().uuid().optional(),
  parentCommentId: z.string().uuid().optional()
});

export const clientCreateSchema = z.object({
  name: z.string().min(1).max(200),
  code: z.string().min(1).max(50),
  email: z.string().email().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  notes: z.string().optional(),
  isActive: z.boolean()
});

export const articleCreateSchema = z.object({
  code: z.string().min(1).max(100),
  description: z.string().min(1).max(500),
  category: z.string().optional(),
  price: z.number().positive().optional(),
  isActive: z.boolean(),
  stockQuantity: z.number().int().min(0).optional(),
  unit: z.string().optional(),
  notes: z.string().optional()
});

// ================== FORM TYPES ==================
export type UserFormData = z.infer<typeof userCreateSchema>;
export type RequestFormData = z.infer<typeof requestCreateSchema>;
export type TaskFormData = z.infer<typeof taskCreateSchema>;
export type CommentFormData = z.infer<typeof commentCreateSchema>;
export type ClientFormData = z.infer<typeof clientCreateSchema>;
export type ArticleFormData = z.infer<typeof articleCreateSchema>;

// ================== UTILITY TYPES ==================
export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;
export type UpdateFields<T> = Partial<Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>>;

// ================== API SERVICE COMPATIBILITY TYPES ==================

// Task API types
export type CreateProjectTaskData = {
  name: string;
  description?: string;
  requestId: string;
  priority: TaskPriority;
  assignedToUserId?: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  tags?: string;
};

export type UpdateProjectTaskData = {
  name: string;
  description?: string;
  priority: TaskPriority;
  assignedToUserId?: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  tags?: string;
};

export type TaskFilters = {
  status?: TaskStatus;
  priority?: TaskPriority;
  assignedUserId?: string;
  requestId?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
};

// ================== HYBRID SELECTOR TYPES ==================
export interface HybridOption<T = any> {
  value: string;
  label: string;
  data: T;
  isExternal: boolean;
  source: 'internal' | 'external';
}

export interface HybridSelectorConfig {
  enableExternal: boolean;
  externalLabel?: string;
  internalLabel?: string;
  showSourceIndicator?: boolean;
  preferInternal?: boolean;
}

// ================== ERROR TYPES ==================
export interface ApiError {
  message: string;
  code?: string;
  details?: Record<string, any>;
  timestamp: string;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface FormError {
  field: string;
  message: string;
}

export interface AsyncError {
  message: string;
  code?: string;
  retry?: () => void;
}

// ================== EXTERNAL DATA TYPES (allineati con backend) ==================
export interface ExternalClient {
  id: string;
  externalId: string;
  code: string;
  name: string;
  companyName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  vatNumber: string;
  fiscalCode: string;
  isActive: boolean;
  lastSyncAt?: string;
  createdAt: string;
  updatedAt: string;
  displayName: string;
  formattedAddress: string;
  syncStatus: string;
  lastUpdated: string;
}

export interface ExternalArticle {
  id: string;
  externalId: string;
  code: string;
  name: string;
  description: string;
  category: string;
  subCategory: string;
  brand: string;
  color: string;
  size: string;
  material: string;
  unitPrice?: number;
  currency: string;
  unit: string;
  isActive: boolean;
  lastSyncAt?: string;
  createdAt: string;
  updatedAt: string;
  formattedPrice: string;
  displayCode: string;
  syncStatus: string;
  searchText: string;
  price?: number;
  stockQuantity: number;
  lastUpdated: string;
}

export interface ExternalDataSyncStatus {
  clientsLastSync: string | null;
  articlesLastSync: string | null;
  isSyncing: boolean;
  totalClients: number;
  totalArticles: number;
  lastError: string | null;
  syncProgress: {
    type: 'clients' | 'articles' | 'all';
    current: number;
    total: number;
    percentage: number;
  } | null;
}

// ================== DYNAMIC KANBAN COLUMN TYPES (Microsoft Teams Planner style) ==================

// Categoria di assegnazione automatica delle colonne. Il backend la salva come stringa
// semplice (non un enum numerico serializzato), quindi la modelliamo come union di stringhe.
export const AUTO_ASSIGN_CATEGORY = {
  Gamma: 'Gamma',
  Personalizzati: 'Personalizzati',
} as const;
export type AutoAssignCategory = typeof AUTO_ASSIGN_CATEGORY[keyof typeof AUTO_ASSIGN_CATEGORY];

export interface TaskAssignee {
  userId: number;
  fullName: string;
  colorTag?: string;
}

export interface KanbanColumnDefinition {
  id: number;
  name: string;
  description?: string;
  color: string;
  sortOrder: number;
  isSystemColumn: boolean;
  isActive: boolean;
  isCompleteColumn: boolean;
  icon?: string;
  wipLimit: number;
  taskCount: number;
  /** Categoria che questa colonna riceve automaticamente ("Gamma" | "Personalizzati" | null). */
  autoAssignCategory?: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateKanbanColumnRequest {
  name: string;
  description?: string;
  color?: string;
  sortOrder?: number;
  isCompleteColumn?: boolean;
  icon?: string;
  wipLimit?: number;
  autoAssignCategory?: string | null;
}

export interface UpdateKanbanColumnRequest {
  name?: string;
  description?: string;
  color?: string;
  sortOrder?: number;
  isCompleteColumn?: boolean;
  icon?: string;
  wipLimit?: number;
  isActive?: boolean;
  autoAssignCategory?: string | null;
}

export interface ReorderKanbanColumnsRequest {
  columnOrders: { columnId: number; newOrder: number }[];
}

export interface MoveTaskToDynamicColumnRequest {
  columnId: number;
  newPosition?: number;
  /** Id del task davanti al quale inserire (ancora di posizione). null/undefined = in fondo. */
  beforeTaskId?: number | null;
}

export interface DynamicKanbanBoard {
  columns: DynamicKanbanColumnData[];
  metadata: KanbanBoardMetadata;
}

export interface DynamicKanbanColumnData {
  id: number;
  name: string;
  description?: string;
  color: string;
  sortOrder: number;
  isSystemColumn: boolean;
  isCompleteColumn: boolean;
  icon?: string;
  wipLimit: number;
  tasks: ProjectTaskSummary[];
  autoAssignCategory?: string | null;
  /** Numero di task completati esclusi dal payload (oltre i 90gg) quando non è attivo un filtro periodo. */
  hiddenCompletedCount?: number;
}

export interface KanbanBoardMetadata {
  totalTasks: number;
  overdueTasks: number;
  highPriorityTasks: number;
  lastUpdated: string;
  requestId?: number;
  activeTasks?: number;
  completedTasks?: number;
  completedOnTime?: number;
  completedLate?: number;
}

export interface ProjectTaskSummary {
  id: number;
  title: string;
  description?: string;
  statusString: string;
  priorityString: string;
  dueDate?: string;
  internalDueDate?: string;
  requestId: number;
  /** @deprecated usare assignees */
  assignedToUserId?: number;
  /** @deprecated usare assignees */
  assignedToUserName?: string;
  /** @deprecated usare assignees */
  assignedToUserColorTag?: string;
  assignees: TaskAssignee[];
  isOverdue: boolean;
  completionPercentage: number;
  checklistItemsCount: number;
  completedChecklistItemsCount: number;
  kanbanColumnId?: number;
  kanbanPosition: number;
  isCompleted: boolean;
  completedAt?: string;
  completedLate?: boolean;
  requestLabel?: string;
  clientName?: string;
  requestType?: number;
  attachmentsCount: number;
  coverThumbnailUrl?: string;
  coverUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

// ================== KANBAN TASK DETAIL / CRUD TYPES ==================

export interface ProjectTaskDetail {
  id: number;
  title: string;
  description?: string;
  statusString: string;
  isCompleted: boolean;
  priorityString: string;
  requestId: number;
  requestLabel?: string;
  requestClientName?: string;
  assignees: TaskAssignee[];
  dueDate?: string;
  completedAt?: string;
  completedLate?: boolean;
  kanbanColumnId?: number;
  kanbanColumnName?: string;
  notes?: string;
  attachmentsCount: number;
  checklistItemsCount: number;
  completedChecklistItemsCount: number;
  coverThumbnailUrl?: string;
  coverUrl?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateKanbanTaskRequest {
  title: string;
  description?: string;
  requestId: number;
  priorityString?: string;
  dueDate?: string;
  assigneeIds?: number[];
  kanbanColumnId?: number;
}

export interface UpdateKanbanTaskRequest {
  title?: string;
  description?: string;
  priorityString?: string;
  dueDate?: string;
  assigneeIds?: number[];
  kanbanColumnId?: number;
  completedAt?: string | null;
  notes?: string;
}

export interface TaskAttachment {
  id: number;
  taskId: number;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  isImage: boolean;
  isPdf: boolean;
  isCover: boolean;
  previewUrl?: string;
  downloadUrl?: string;
  thumbnailUrl?: string;
  uploadedBy: string;
  createdAt: string;
}

// ================== KANBAN BOARD FILTERS ==================

export type KanbanDateField = 'planned' | 'created' | 'completed';

export interface KanbanBoardFilters {
  requestId?: number;
  assigneeIds?: number[];
  dateField?: KanbanDateField;
  /** Formato YYYY-MM-DD */
  from?: string;
  /** Formato YYYY-MM-DD */
  to?: string;
}

export interface RequestLookupItem {
  id: number;
  label: string;
  clientName?: string;
  requestType?: number;
  status?: string;
}

// ================== DEADLINE ALERT TYPES ==================

export interface DeadlineAlert {
  taskId: number;
  taskTitle: string;
  requestId: number;
  requestTitle: string;
  dueDate: string;
  internalDueDate?: string;
  daysUntilDue: number;
  isOverdue: boolean;
  priority: string;
  assignedTo?: UserSummary;
  isSnoozed: boolean;
  snoozedUntil?: string;
}

export interface UserSummary {
  id: number;
  fullName: string;
  email: string;
  colorTag?: string;
}

export interface SnoozeAlertRequest {
  taskId: number;
  snoozeMinutes: number;
}

export interface DismissAlertRequest {
  taskId: number;
}

export interface UserAlertPreferences {
  deadlineAlertDays: number;
}

// ================== PROGRESSIVE NUMBER TYPES ==================

export interface ProgressiveNumberSuggestion {
  number: number;
  year: number;
  formatted: string;
}

export interface ProgressiveNumberValidation {
  isAvailable: boolean;
  errorMessage?: string;
}
